import React, { Component } from 'react';
import { connect } from 'react-redux';
import { func, string, bool, object, array } from 'prop-types';
import { translate, Trans } from 'react-i18next';

import {
  CircleCheckIcon,
  CircleQuestionIcon,
} from '@snapask/common-components';

import {
  getDistricts,
  fetchStudentDistricts,
} from '@dragonfruit/redux/district';
import { UPDATE_PHONE_STAGE_ONE } from '@dragonfruit/constants/updatePhoneStages';
import {
  changeSchool,
  changeSchoolComplete,
  changeSchoolInitialize,
  getErrorCode as getChangeSchoolErrorCode,
  getOpenStatus as getChangeSchoolOpenStatus,
  closeEditPanel as closeEditSchoolPanel,
} from '@dragonfruit/redux/changeSchool';
import {
  updatePhoneInitialize,
  updatePhoneComplete,
  getOpenStatus as getUpdatePhoneOpenStatus,
  getErrorCode as getUpdatePhoneErrorCode,
  getStage,
  updatePhone,
  resendConfirmationCode,
  confirmVerificationCode,
  closeEditPanel as closeEditPhonePanel,
} from '@dragonfruit/redux/updatePhone';
import {
  resetEmail,
  resetEmailComplete,
  resetEmailInitialize,
  getOpenStatus as getResetEmailOpenStatus,
  getErrorCode as getResetEmailErrorCode,
  closeEditPanel as closeEditEmailPanel,
} from '@dragonfruit/redux/resetEmail';
import { sendConfirmationEmail } from '@dragonfruit/redux/sendEmail';
import { getSchools, fetchStudentSchools } from '@dragonfruit/redux/schools';
import { getProfile } from '@dragonfruit/redux/profile';
import { showAlertBanner } from '@dragonfruit/redux/alertBanner';
import { STUDENT } from '@dragonfruit/constants/userRoles';

import ResetEmailForm from './components/ResetEmailForm';
import UpdatePhoneForm from './components/UpdatePhoneForm';
import ChangeSchoolForm from './components/ChangeSchoolForm';
import PhoneConfirmation from './components/PhoneConfirmation';

import styles from './AccountProfileVerify.scss';

const cx = require('classnames/bind').bind(styles);

const mapStateToProps = state => ({
  changeSchoolErrorCode: getChangeSchoolErrorCode(state),
  changeSchoolOpen: getChangeSchoolOpenStatus(state),

  updatePhoneOpen: getUpdatePhoneOpenStatus(state),
  updatePhoneErrorCode: getUpdatePhoneErrorCode(state),
  updatePhoneStage: getStage(state),

  resetEmailOpen: getResetEmailOpenStatus(state),
  resetEmailErrorCode: getResetEmailErrorCode(state),

  districtList: getDistricts(state),
  schoolList: getSchools(state),

  profile: getProfile(state),
});

class AccountProfileVerify extends Component {
  static propTypes = {
    changeSchool: func.isRequired,
    changeSchoolComplete: func.isRequired,
    changeSchoolErrorCode: string.isRequired,
    changeSchoolInitialize: func.isRequired,
    changeSchoolOpen: bool.isRequired,
    closeEditEmailPanel: func.isRequired,
    closeEditPhonePanel: func.isRequired,
    closeEditSchoolPanel: func.isRequired,
    confirmVerificationCode: func.isRequired,
    districtList: array.isRequired,
    fetchStudentDistricts: func.isRequired,
    fetchStudentSchools: func.isRequired,
    profile: object.isRequired,
    resendConfirmationCode: func.isRequired,
    resetEmail: func.isRequired,
    resetEmailComplete: func.isRequired,
    resetEmailErrorCode: string.isRequired,
    resetEmailInitialize: func.isRequired,
    resetEmailOpen: bool.isRequired,
    schoolList: array.isRequired,
    sendConfirmationEmail: func.isRequired,
    showAlertBanner: func.isRequired,
    t: func.isRequired,
    updatePhone: func.isRequired,
    updatePhoneComplete: func.isRequired,
    updatePhoneErrorCode: string.isRequired,
    updatePhoneInitialize: func.isRequired,
    updatePhoneOpen: bool.isRequired,
    updatePhoneStage: string.isRequired,
  };

  static defaultProps = {
    resetPasswordStatus: '',
    resetPasswordErrorCode: '',
    resetPasswordOpen: false,
    resetEmailStatus: '',
    resetEmailErrorCode: '',
    resetEmailOpen: false,
  };

  state = {
    code: '',
    codeEmpty: true,
    codeTouched: false,
  };

  componentWillUnmount() {
    const {
      closeEditEmailPanel,
      closeEditPhonePanel,
      closeEditSchoolPanel,
    } = this.props;

    closeEditEmailPanel();
    closeEditPhonePanel();
    closeEditSchoolPanel();
  }

  onSubmitChangeSchool = ({ school }) => {
    const {
      profile: { userId },
      changeSchool,
    } = this.props;

    const data = {
      userId,
    };

    if (isNaN(school.value)) {
      data.schoolName = school.label;
    } else {
      data.schoolId = school.value;
    }

    changeSchool(data);
  };

  onSelectDistrict = district => {
    this.props.fetchStudentSchools(district);
  };

  onClickConfirmCode = code => {
    const {
      confirmVerificationCode,
      profile: { userId },
    } = this.props;

    confirmVerificationCode({
      userId,
      code,
    });
  };

  onClickUpdatePhone = phone => {
    const {
      updatePhone,
      profile: { userId },
    } = this.props;
    updatePhone({
      phone,
      userId,
    });
  };

  onClickResendConfirmationCode = () => {
    const {
      resendConfirmationCode,
      profile: { userId },
    } = this.props;
    resendConfirmationCode(userId);
  };

  onSendConfirmationEmail = evt => {
    evt.preventDefault();
    const {
      sendConfirmationEmail,
      profile: { userId },
    } = this.props;

    sendConfirmationEmail(userId);
  };

  onResetEmailSubmit = email => {
    const {
      profile: { userId },
      resetEmail,
    } = this.props;

    resetEmail({
      email,
      userId,
    });
  };

  onToggleChangeSchool = evt => {
    evt.preventDefault();
    const {
      changeSchoolOpen,
      changeSchoolComplete,
      changeSchoolInitialize,
      fetchStudentDistricts,
      profile: { regionId: country },
    } = this.props;

    if (changeSchoolOpen) {
      changeSchoolComplete();

      return;
    }

    fetchStudentDistricts(country);

    changeSchoolInitialize();

    return;
  };

  onTogglePhoneUpdate = evt => {
    evt.preventDefault();
    const {
      updatePhoneOpen,
      updatePhoneComplete,
      updatePhoneInitialize,
      profile: { unconfirmedPhone },
    } = this.props;

    if (updatePhoneOpen) {
      updatePhoneComplete();

      return;
    }

    updatePhoneInitialize(!!unconfirmedPhone);

    return;
  };

  onToggleResetEmail = evt => {
    evt.preventDefault();
    const {
      resetEmailOpen,
      resetEmailComplete,
      resetEmailInitialize,
    } = this.props;

    if (resetEmailOpen) {
      resetEmailComplete();

      return;
    }

    resetEmailInitialize();
  };

  renderSchoolField = () => {
    const {
      t,
      changeSchoolOpen,
      changeSchoolErrorCode,
      districtList,
      schoolList,
      profile: { school, schoolVerified },
    } = this.props;

    let schoolName = school || t('account.notSelected');

    const schoolField = (
      <React.Fragment>
        <p className={styles.field}>{t('account.school')}</p>
        <div className={styles.reset}>
          <div className={cx(styles.value, !school && styles.disabled)}>
            <span>{schoolName}</span>
            <div className={styles.icon}>
              {school && schoolVerified ? (
                <CircleCheckIcon width={18} alt={school} />
              ) : (
                <CircleQuestionIcon width={18} alt={school} />
              )}
            </div>
          </div>
          <a className={styles.linkButton} onClick={this.onToggleChangeSchool}>
            {t('account.edit')}
          </a>
        </div>
      </React.Fragment>
    );

    return (
      <div className={styles.input}>
        <div className={styles.fieldContainer}>
          {schoolField}
          {changeSchoolOpen && (
            <div className={styles.resetFormPadded}>
              <div className={styles.header}>
                <p className={styles.field}>{t('account.changeSchool')}</p>
                <a onClick={this.onToggleChangeSchool}>{t('account.cancel')}</a>
              </div>
              <ChangeSchoolForm
                onSubmit={this.onSubmitChangeSchool}
                onSelectDistrict={this.onSelectDistrict}
                districtList={districtList}
                schoolList={schoolList}
              />
              {changeSchoolErrorCode && (
                <p className={styles.error}>{changeSchoolErrorCode}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderPhoneField = () => {
    const {
      t,
      updatePhoneOpen,
      updatePhoneErrorCode,
      updatePhoneStage,
      profile: { phone, phoneConfirmedAt, countryCode },
      updatePhoneInitialize,
    } = this.props;

    const phoneContent = (
      <React.Fragment>
        <span>{`+${countryCode} ${phone}`}</span>
        <div className={styles.icon}>
          {phoneConfirmedAt ? (
            <CircleCheckIcon width={18} alt={phoneConfirmedAt} />
          ) : (
            <CircleQuestionIcon width={18} alt={phoneConfirmedAt} />
          )}
        </div>
      </React.Fragment>
    );

    const phoneField = (
      <React.Fragment>
        <p className={styles.field}>{t('account.phoneNumber')}</p>
        <div className={styles.reset}>
          <div className={cx(styles.value, !phone && styles.disabled)}>
            {phone ? phoneContent : <span>{t('account.notSelected')}</span>}
          </div>
          {!updatePhoneOpen && (
            <a className={styles.linkButton} onClick={this.onTogglePhoneUpdate}>
              <h6>{phone ? t('account.reset.text') : t('account.update')}</h6>
            </a>
          )}
        </div>
      </React.Fragment>
    );

    return (
      <div className={styles.input}>
        <div className={styles.fieldContainer}>
          {phoneField}
          {updatePhoneOpen && (
            <div className={styles.resetFormPadded}>
              <div className={styles.header}>
                <p className={styles.field}>{t('account.updatePhone')}</p>
                <a onClick={this.onTogglePhoneUpdate}>
                  <h6>{t('account.cancel')}</h6>
                </a>
              </div>
              {updatePhoneStage === UPDATE_PHONE_STAGE_ONE ? (
                <UpdatePhoneForm
                  countryCode={countryCode}
                  onSubmit={this.onClickUpdatePhone}
                />
              ) : (
                <PhoneConfirmation
                  clickConfirmCode={this.onClickConfirmCode}
                  resetStage={updatePhoneInitialize}
                  onClickResendConfirmationCode={
                    this.onClickResendConfirmationCode
                  }
                />
              )}
              {updatePhoneErrorCode && (
                <p className={styles.error}>{updatePhoneErrorCode}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderEmailField = () => {
    const {
      profile: { email, unconfirmedEmail },
      resetEmailOpen,
      resetEmailErrorCode,
      t,
    } = this.props;

    const resetEmailBtn = (
      <a className={styles.linkButton} onClick={this.onToggleResetEmail}>
        <h6>{t('account.resendEmail')}</h6>
      </a>
    );

    let emailField = (
      <React.Fragment>
        <p className={styles.field}>{t('account.email')}</p>
        <div className={styles.reset}>
          <div className={cx(styles.value, !email && styles.disabled)}>
            <span>{email || t('account.notSelected')}</span>
            <div className={styles.icon}>
              {email ? (
                <CircleCheckIcon width={18} alt={email} />
              ) : (
                <CircleQuestionIcon width={18} />
              )}
            </div>
          </div>
          {!resetEmailOpen && resetEmailBtn}
        </div>
      </React.Fragment>
    );

    if (unconfirmedEmail) {
      emailField = (
        <React.Fragment>
          <p className={styles.field}>{t('account.email')}</p>
          <p className={styles.helper}>
            <Trans i18nKey="account.emailHelper">
              Verifying your email address to allow us send you important
              information about your account.
              <a onClick={this.onSendConfirmationEmail}> resent email! </a>
            </Trans>
          </p>
          <div className={styles.reset}>
            <div className={styles.value}>
              <span>{unconfirmedEmail}</span>
              <div className={styles.icon}>
                <CircleQuestionIcon width={18} alt={unconfirmedEmail} />
              </div>
            </div>
            {!resetEmailOpen && resetEmailBtn}
          </div>
        </React.Fragment>
      );
    }

    return (
      <div className={styles.input}>
        <div className={styles.fieldContainer}>
          {emailField}
          {resetEmailOpen && (
            <div className={styles.resetForm}>
              <div className={styles.header}>
                <p className={styles.field}>{t('account.resetEmail')}</p>
                <a onClick={this.onToggleResetEmail}>{t('account.cancel')}</a>
              </div>
              <p className={styles.helper}>{t('account.resetEmailHelper')}</p>
              <ResetEmailForm onSubmit={this.onResetEmailSubmit} />
              {resetEmailErrorCode && (
                <p className={styles.error}>{resetEmailErrorCode}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { profile: { role } = {} } = this.props;
    return (
      <div className={styles.userFieldContainer}>
        {/* school field */}
        {role === STUDENT && this.renderSchoolField()}
        {/* phone field */}
        {this.renderPhoneField()}
        {/* email field */}
        {this.renderEmailField()}
      </div>
    );
  }
}

export default connect(mapStateToProps, {
  updatePhone,
  updatePhoneComplete,
  updatePhoneInitialize,
  resetEmail,
  resetEmailComplete,
  resetEmailInitialize,
  sendConfirmationEmail,
  changeSchool,
  changeSchoolComplete,
  changeSchoolInitialize,
  fetchStudentDistricts,
  resendConfirmationCode,
  confirmVerificationCode,
  fetchStudentSchools,
  closeEditEmailPanel,
  closeEditPhonePanel,
  closeEditSchoolPanel,
  showAlertBanner,
})(translate(['account'])(AccountProfileVerify));



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountProfile/components/AccountProfileVerify/AccountProfileVerify.jsx